import { createContext, useContext, useState, useMemo } from 'react';
import PropTypes from 'prop-types';

const PolygonContext = createContext();

/**
 * Context provider for global polygon state.
 *
 * Provides shared state for polygon functionality across the application:
 * - Polygon completion state
 * - Forced marker fitting control
 * - Saved map bounds restoration
 *
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components that will have access to the context
 *
 * @returns {JSX.Element} Polygon context provider
 *
 * @example
 * ```jsx
 * function App() {
 *   return (
 *     <PolygonProvider>
 *       <MapComponent />
 *       <SearchComponent />
 *     </PolygonProvider>
 *   );
 * }
 * ```
 */
export const PolygonProvider = ({ children }) => {
  const [polygonCompleted, setPolygonCompleted] = useState(false);
  const [forceFitMarkers, setForceFitMarkers] = useState(false);
  const [restoreSavedBounds, setRestoreSavedBounds] = useState(false);

  const value = useMemo(
    () => ({
      polygonCompleted,
      setPolygonCompleted,
      forceFitMarkers,
      setForceFitMarkers,
      restoreSavedBounds,
      setRestoreSavedBounds,
    }),
    [polygonCompleted, forceFitMarkers, restoreSavedBounds],
  );

  return <PolygonContext.Provider value={value}>{children}</PolygonContext.Provider>;
};

PolygonProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

/**
 * Hook to access polygon state context.
 *
 * Provides access to global polygon state and its update functions.
 * Must be used within a component wrapped by PolygonProvider.
 *
 * @returns {Object} Object with polygon context state and functions:
 * @returns {boolean} returns.polygonCompleted - Indicates if a polygon was completed
 * @returns {Function} returns.setPolygonCompleted - Function to update completion state
 * @returns {boolean} returns.forceFitMarkers - Indicates if marker fitting should be forced
 * @returns {Function} returns.setForceFitMarkers - Function to update forced fitting
 * @returns {boolean} returns.restoreSavedBounds - Indicates if saved bounds should be restored
 * @returns {Function} returns.setRestoreSavedBounds - Function to update restoration
 *
 * @throws {Error} If used outside of a PolygonProvider
 *
 * @example
 * ```jsx
 * function MapComponent() {
 *   const {
 *     polygonCompleted,
 *     setPolygonCompleted,
 *     forceFitMarkers,
 *     setForceFitMarkers
 *   } = usePolygonStatus();
 *
 *   useEffect(() => {
 *     if (polygonCompleted) {
 *       // Handle polygon completion
 *       setForceFitMarkers(true);
 *     }
 *   }, [polygonCompleted, setForceFitMarkers]);
 *
 *   return <div>Map content</div>;
 * }
 * ```
 */
export const usePolygonStatus = () => useContext(PolygonContext);

export { PolygonContext };
